import { useEffect, useRef } from 'react'

interface Node {
  x: number
  y: number
  ox: number
  oy: number
  vx: number
  vy: number
}

export function GridBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    let cleanup: (() => void) | null = null

    const init = () => {
      const canvas = canvasRef.current
      if (!canvas) return
      const container = canvas.parentElement
      if (!container) return
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      let animFrameId: number
      let nodes: Node[] = []
      let W = 0
      let H = 0
      const mouse = { x: -9999, y: -9999 }

      function resize() {
        W = canvas.width = container.offsetWidth
        H = canvas.height = container.offsetHeight
        initNodes()
      }

      function initNodes() {
        nodes = []
        const sp = 72
        const cols = Math.ceil(W / sp) + 2
        const rows = Math.ceil(H / sp) + 2
        for (let i = 0; i < cols; i++) {
          for (let j = 0; j < rows; j++) {
            const ox = i * sp - sp / 2
            const oy = j * sp - sp / 2
            nodes.push({ x: ox, y: oy, ox, oy, vx: 0, vy: 0 })
          }
        }
      }

      function onMouseMove(e: MouseEvent) {
        const rect = canvas.getBoundingClientRect()
        mouse.x = e.clientX - rect.left
        mouse.y = e.clientY - rect.top
      }

      function draw() {
        ctx.clearRect(0, 0, W, H)
        const cols = Math.ceil(W / 72) + 2

        nodes.forEach(n => {
          const dx = mouse.x - n.x
          const dy = mouse.y - n.y
          const d = Math.sqrt(dx * dx + dy * dy)
          const md = 200
          if (d < md) {
            const f = ((md - d) / md) * 0.015
            n.vx -= dx * f
            n.vy -= dy * f
          }
          n.vx += (n.ox - n.x) * 0.055
          n.vy += (n.oy - n.y) * 0.055
          n.vx *= 0.8
          n.vy *= 0.8
          n.x += n.vx
          n.y += n.vy
        })

        for (let i = 0; i < nodes.length; i++) {
          const n = nodes[i]
          const r = nodes[i + 1]
          const d2 = nodes[i + cols]
          const mx = mouse.x
          const my = mouse.y

          if (r && Math.abs(r.x - n.x) < 140) {
            const d = Math.sqrt((mx - n.x) ** 2 + (my - n.y) ** 2)
            const p = Math.max(0, 1 - d / 320)
            ctx.beginPath()
            ctx.moveTo(n.x, n.y)
            ctx.lineTo(r.x, r.y)
            ctx.strokeStyle = `rgba(78,125,233,${0.035 + p * 0.2})`
            ctx.lineWidth = 0.4 + p * 1.2
            ctx.stroke()
          }

          if (d2 && Math.abs(d2.y - n.y) < 140) {
            const d = Math.sqrt((mx - n.x) ** 2 + (my - n.y) ** 2)
            const p = Math.max(0, 1 - d / 320)
            ctx.beginPath()
            ctx.moveTo(n.x, n.y)
            ctx.lineTo(d2.x, d2.y)
            ctx.strokeStyle = `rgba(78,125,233,${0.035 + p * 0.2})`
            ctx.lineWidth = 0.4 + p * 1.2
            ctx.stroke()
          }
        }

        nodes.forEach(n => {
          const d = Math.sqrt((mouse.x - n.x) ** 2 + (mouse.y - n.y) ** 2)
          const p = Math.max(0, 1 - d / 220)
          ctx.beginPath()
          ctx.arc(n.x, n.y, 1.2 + p * 2.8, 0, Math.PI * 2)
          ctx.fillStyle = `rgba(78,125,233,${0.06 + p * 0.55})`
          ctx.fill()
        })

        animFrameId = requestAnimationFrame(draw)
      }

      const ro = new ResizeObserver(() => resize())
      ro.observe(canvas)
      resize()
      window.addEventListener('mousemove', onMouseMove)
      draw()

      cleanup = () => {
        cancelAnimationFrame(animFrameId)
        window.removeEventListener('mousemove', onMouseMove)
        ro.disconnect()
      }
    }

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const id = window.requestIdleCallback(init, { timeout: 2000 })
      return () => {
        window.cancelIdleCallback(id)
        cleanup?.()
      }
    } else {
      const t = setTimeout(init, 200)
      return () => {
        clearTimeout(t)
        cleanup?.()
      }
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      width={1}
      height={1}
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        display: 'block',
      }}
    />
  )
}
